import { useMemo } from 'react';
import { Autocomplete, Stack, TextField, Typography } from '@mui/material';

import { useFieldArray, useFormContext } from 'FrontRoyalReactHookForm';
import {
    RemoveButton,
    ReorderButtons,
    SectionHeading,
    preventBackspaceRemoval,
    renderOptionKeyedById,
} from 'ContentItemEditor';
import { courseTitleWithTag } from './useEditorCourses';
import { type EditorCourse, type StreamFormValues } from './StreamEditor.types';

type PickerFieldName = 'recommended_stream_ids' | 'related_stream_ids';

type Props = {
    name: PickerFieldName;
    label: string;
    courses: EditorCourse[];
    coursesById: Record<string, EditorCourse>;

    // Only courses of this locale may go on the list. The course list is loaded for every locale,
    // because the translation links need all of them, so the narrowing happens here.
    locale: string;
};

// An ordered list of other courses, for the Recommended Study Courses and Related Courses fields.
export default function CoursePickerSection({ name, label, courses, coursesById, locale }: Props) {
    const { control, watch } = useFormContext<StreamFormValues>();
    const { fields, append, remove, move } = useFieldArray({ control, name });

    /*
        The course ids come from the form values, not from `fields`. useFieldArray writes its own
        key over the `id` of every row it hands back, so `fields[n].id` is a generated uuid rather
        than the course. `fields` is still what we map over, because its keys are stable across a
        reorder.
    */
    const rows = watch(name);

    // A course already on the list is not worth offering again. The Angular page loaded this list
    // per locale, so mixing locales was never possible there.
    const options = useMemo(() => {
        const chosen = new Set((rows || []).map(row => row.id));
        return courses.filter(course => !chosen.has(course.id) && (course.locale || 'en') === locale);
    }, [courses, rows, locale]);

    return (
        <Stack spacing={1}>
            <SectionHeading>{label}</SectionHeading>

            <Autocomplete
                // The picker holds no value of its own: choosing a course appends it to the list
                // below and clears the input, as the Angular selectize did.
                value={null}
                blurOnSelect
                options={options}
                getOptionLabel={course => courseTitleWithTag(course)}
                isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
                onChange={(_event, course) => course && append({ id: course.id })}
                onKeyDown={preventBackspaceRemoval}
                renderOption={renderOptionKeyedById(courseTitleWithTag)}
                renderInput={props => <TextField {...props} name={`add_${name}`} label="Add course" />}
            />

            {fields.map((field, index) => (
                <Stack key={field.id} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography sx={{ flexGrow: 1 }}>
                        {courseTitleWithTag(coursesById[rows?.[index]?.id || ''])}
                    </Typography>

                    <Stack direction="row" sx={{ alignItems: 'center' }}>
                        <ReorderButtons index={index} count={fields.length} label="this course" onMove={move} />

                        <RemoveButton
                            name={`remove_${name}`}
                            label="Remove this course"
                            onClick={() => remove(index)}
                        />
                    </Stack>
                </Stack>
            ))}
        </Stack>
    );
}
